import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface AuthenticatedImageProps {
  // API-relative path (goes through the same `api` instance as every
  // other request, so the Authorization header is attached automatically)
  // — NOT a raw /uploads/... URL. A plain <img src="..."> can't send that
  // header, which is exactly why /uploads was replaced with auth-gated
  // download endpoints (see docs/OPEN_ITEMS.md, "/uploads" fix).
  src: string;
  alt: string;
  className?: string;
  // When true, clicking the image opens the already-fetched blob in a
  // new tab (no extra request — the object URL doesn't need auth, it's
  // already resolved in memory) instead of the old `<a href={imageUrl}
  // target="_blank">` wrapper, which would break the same way a plain
  // <img> does.
  clickToOpen?: boolean;
}

const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({ src, alt, className, clickToOpen }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let currentUrl: string | null = null;

    setObjectUrl(null);
    setFailed(false);

    api.get(src, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        currentUrl = URL.createObjectURL(res.data);
        setObjectUrl(currentUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [src]);

  if (failed) {
    return <div className={`${className || ''} bg-gray-100 dark:bg-gray-700`} title={alt} />;
  }

  if (!objectUrl) {
    return <div className={`${className || ''} bg-gray-100 dark:bg-gray-700 animate-pulse`} />;
  }

  return (
    <img
      src={objectUrl}
      alt={alt}
      className={className}
      onClick={clickToOpen ? () => window.open(objectUrl, '_blank') : undefined}
    />
  );
};

export default AuthenticatedImage;
